import React, { useState } from 'react';
import { 
  Leaf, 
  Home, 
  Check, 
  X, 
  Plus, 
  Calendar, 
  CreditCard, 
  Shield, 
  Sparkles,
  CloudRain,
  Maximize2,
  Sun,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import { Button } from './ui/button';

export default function ServicePackages({ onEnquire }) {
  const [expandedPackage, setExpandedPackage] = useState(null);

  const toggleExpand = (pkg) => {
    setExpandedPackage(expandedPackage === pkg ? null : pkg);
  };

  return (
    <section id="services" className="py-20 bg-slate-50" data-testid="service-packages-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-green-900 mb-4">
            Our Services
          </h2>
          <div className="gold-accent-line w-24 mx-auto mb-6" />
          <p className="font-inter text-gray-600 max-w-3xl mx-auto text-base sm:text-lg">
            Choose a maintenance package that suits your property. Our subscription plans provide consistent, 
            professional care throughout the year with predictable monthly payments.
          </p>
        </div>

        {/* Package Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          
          {/* Lawn Care Package */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100" data-testid="lawn-care-package">
            <div className="bg-green-900 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Leaf className="w-6 h-6" />
                </div>
                <h3 className="font-oswald text-2xl font-bold">Lawn Care Package</h3>
              </div>
              <p className="font-inter text-white/80 text-sm">
                Year-round lawn & garden maintenance subscription
              </p>
            </div>

            <div className="p-6">
              {/* Key Value Proposition */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="font-oswald text-lg font-semibold text-green-900 mb-1">
                  24 Scheduled Visits Per Year
                </p>
                <p className="font-inter text-sm text-green-700">
                  Fortnightly during peak growing months, approximately monthly during cooler months
                </p>
              </div>

              {/* Service Schedule */}
              <div className="mb-6">
                <h4 className="font-oswald text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-amber-600" />
                  Service Schedule
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-amber-50 rounded-lg p-4">
                    <p className="font-oswald font-semibold text-amber-900">Peak Season</p>
                    <p className="font-inter text-sm text-amber-700">September – April/May</p>
                    <p className="font-inter text-xs text-amber-600 mt-1">Fortnightly service</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="font-oswald font-semibold text-blue-900">Cooler Months</p>
                    <p className="font-inter text-sm text-blue-700">June – August/September</p>
                    <p className="font-inter text-xs text-blue-600 mt-1">Every 3-4 weeks</p>
                  </div>
                </div>
              </div>

              {/* Standard Inclusions */}
              <div className="mb-6">
                <h4 className="font-oswald text-lg font-semibold text-gray-900 mb-3">
                  Every Visit Includes
                </h4>
                <ul className="space-y-2">
                  {[
                    'Lawn mowing',
                    'Edging / whipper snipping',
                    'Blowing / clean-up',
                    'Targeted weed control',
                    'Garden bed / loose weed management'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 font-inter text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Expandable Add-ons */}
              <button
                onClick={() => toggleExpand('lawn')}
                className="w-full flex items-center justify-between py-3 border-t border-gray-100 text-left"
              >
                <span className="font-oswald font-medium text-gray-700">Available Add-ons</span>
                {expandedPackage === 'lawn' ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {expandedPackage === 'lawn' && (
                <div className="pb-4 animate-fade-in-up">
                  <p className="font-inter text-xs text-gray-500 mb-2">Not included in standard subscription:</p>
                  <div className="flex flex-wrap gap-2">
                    {['Hedge trimming', 'Pruning', 'Mulching', 'Fertilising', 'Pressure cleaning'].map((addon, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-xs font-inter text-gray-600">
                        <Plus className="w-3 h-3" />
                        {addon}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing */}
              <div className="border-t border-gray-100 pt-6 mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  <h4 className="font-oswald text-lg font-semibold text-gray-900">Pricing</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="font-inter text-xs text-gray-500 uppercase tracking-wide mb-1">Monthly Direct Debit</p>
                    <p className="font-oswald text-2xl font-bold text-green-900">$[PRICE]</p>
                    <p className="font-inter text-xs text-gray-500">+ GST / month</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="font-inter text-xs text-gray-500 uppercase tracking-wide mb-1">Per Visit Rate</p>
                    <p className="font-oswald text-2xl font-bold text-green-900">$[PRICE]</p>
                    <p className="font-inter text-xs text-gray-500">+ GST / visit</p>
                  </div>
                </div>

                {/* Who it's for */}
                <div className="bg-green-50 rounded-lg p-4 mb-6">
                  <p className="font-inter text-sm text-green-800">
                    <strong>Ideal for:</strong> Homeowners who want consistent lawn maintenance without the hassle of scheduling individual visits.
                  </p>
                </div>

                <Button
                  onClick={() => onEnquire('Lawn Care Package')}
                  className="w-full bg-green-900 hover:bg-green-800 text-white font-oswald font-semibold py-6 text-lg"
                  data-testid="enquire-lawn-care-btn"
                >
                  Book a Property Assessment
                </Button>
              </div>
            </div>
          </div>

          {/* Ultimate Property Package */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-amber-500 relative" data-testid="ultimate-package">
            {/* Premium Badge */}
            <div className="absolute top-4 right-4 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-oswald font-semibold">
              PREMIUM
            </div>

            <div className="bg-gradient-to-r from-amber-600 to-amber-500 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Home className="w-6 h-6" />
                </div>
                <h3 className="font-oswald text-2xl font-bold">Ultimate Property Package</h3>
              </div>
              <p className="font-inter text-white/90 text-sm">
                Complete property maintenance solution
              </p>
            </div>

            <div className="p-6">
              {/* Key Value Proposition */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="font-oswald text-lg font-semibold text-amber-900 mb-1">
                  Everything in Lawn Care + Gutter & Window Cleaning
                </p>
                <p className="font-inter text-sm text-amber-700">
                  One predictable plan instead of multiple contractors and unexpected bills
                </p>
              </div>

              {/* What's Included */}
              <div className="mb-6">
                <h4 className="font-oswald text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  Complete Package Includes
                </h4>
                
                {/* Lawn Care Component */}
                <div className="bg-green-50 rounded-lg p-4 mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Leaf className="w-4 h-4 text-green-700" />
                    <p className="font-oswald font-semibold text-green-900 text-sm">24-Visit Lawn Care Plan</p>
                  </div>
                  <p className="font-inter text-xs text-green-700">
                    Mowing, edging, blowing, weed control & garden bed management
                  </p>
                </div>

                {/* Gutter Cleaning */}
                <div className="bg-blue-50 rounded-lg p-4 mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CloudRain className="w-4 h-4 text-blue-700" />
                    <p className="font-oswald font-semibold text-blue-900 text-sm">Gutter Cleaning (1-2x per year)</p>
                  </div>
                  <div className="space-y-1 font-inter text-xs text-blue-700">
                    <p>• Post-Autumn Cleanout (May/June) - <em>Unlocked after 3 months</em></p>
                    <p>• Pre-Fire/Storm Season (Nov/Dec) - <em>Unlocked after 9 months</em></p>
                  </div>
                </div>

                {/* Window Cleaning */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Maximize2 className="w-4 h-4 text-purple-700" />
                    <p className="font-oswald font-semibold text-purple-900 text-sm">Window Cleaning (1-2x per year)</p>
                  </div>
                  <div className="space-y-1 font-inter text-xs text-purple-700">
                    <p>• Internal & external glass</p>
                    <p>• Window tracks & flyscreens</p>
                    <p className="italic">Perfect for coastal properties with salt & grime build-up</p>
                  </div>
                </div>
              </div>

              {/* Unlock Schedule Info */}
              <div className="bg-slate-100 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
                  <p className="font-inter text-xs text-slate-600">
                    Gutter and window cleaning services unlock after maintaining continuous subscription. 
                    This ensures consistent service quality and availability.
                  </p>
                </div>
              </div>

              {/* Expandable Add-ons */}
              <button
                onClick={() => toggleExpand('ultimate')}
                className="w-full flex items-center justify-between py-3 border-t border-gray-100 text-left"
              >
                <span className="font-oswald font-medium text-gray-700">Available Add-ons</span>
                {expandedPackage === 'ultimate' ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {expandedPackage === 'ultimate' && (
                <div className="pb-4 animate-fade-in-up">
                  <p className="font-inter text-xs text-gray-500 mb-2">Optional additional services:</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'Pressure cleaning', 'Driveway cleaning', 'House washing', 
                      'Solar panel cleaning', 'Hedge trimming', 'Pruning', 'Mulching', 'Fertilising'
                    ].map((addon, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-xs font-inter text-gray-600">
                        <Plus className="w-3 h-3" />
                        {addon}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing */}
              <div className="border-t border-gray-100 pt-6 mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  <h4 className="font-oswald text-lg font-semibold text-gray-900">Pricing</h4>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center mb-4">
                  <p className="font-inter text-xs text-amber-600 uppercase tracking-wide mb-1">Monthly Direct Debit</p>
                  <p className="font-oswald text-3xl font-bold text-amber-900">$[PRICE]</p>
                  <p className="font-inter text-xs text-amber-600">+ GST / month</p>
                </div>

                {/* Who it's for */}
                <div className="bg-amber-50 rounded-lg p-4 mb-6">
                  <p className="font-inter text-sm text-amber-800">
                    <strong>Ideal for:</strong> Property owners who want complete, hassle-free maintenance with one predictable monthly payment covering lawn, gutters, and windows.
                  </p>
                </div>

                <Button
                  onClick={() => onEnquire('Ultimate Property Package')}
                  className="w-full bg-amber-600 hover:bg-amber-500 text-white font-oswald font-semibold py-6 text-lg"
                  data-testid="enquire-ultimate-btn"
                >
                  Book a Property Assessment
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Placeholder Notice */}
        <div className="text-center mb-8">
          <p className="font-inter text-sm text-gray-500 italic">
            * Pricing is customised based on property size and requirements. Book a property assessment for an accurate quote.
          </p>
        </div>
      </div>
    </section>
  );
}
